import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/shadcn-ui/utils';
import ChevronDown from '@/icons/chevron-down';
import { colors } from '@/theme/colors';

const Accordion = AccordionPrimitive.Root;

interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> {
  className?: string;
}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, ...props }: AccordionItemProps, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn('border-b', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  className?: string;
  hideIcon?: boolean;
  iconOverride?: {
    openedIcon: React.ReactNode;
    closedIcon: React.ReactNode;
  };
  iconSize?: 'default' | 'small';
  iconVariant?: 'default' | 'dark';
  /**
   * This prop corresponds to the open state of the accordion item this trigger is associated with
   * and determines when to show the openedIcon and closedIcon.
   *
   * Note: open won't have any effect if an iconOverride is not provided.
   */
  open?: boolean;
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      hideIcon = false,
      iconOverride,
      iconSize = 'default',
      iconVariant = 'default',
      children,
      open,
      ...props
    }: AccordionTriggerProps,
    ref
  ) => {
    const iconFill = iconVariant === 'dark' ? colors.neutral1 : colors.neutral100;
    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'group flex flex-1 items-center justify-between gap-4 py-6 transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
            className
          )}
          {...props}
        >
          {children}
          {hideIcon ? null : iconOverride ? (
            open ? (
              iconOverride.openedIcon
            ) : (
              iconOverride.closedIcon
            )
          ) : (
            <ChevronDown
              className={cn('shrink-0 transition-transform duration-150', {
                'h-[24px] w-[24px]': iconSize === 'default',
                'h-[16px] w-[16px]': iconSize === 'small',
              })}
              fillPrimary={iconFill}
            />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {
  className?: string;
  fadeWhileClosing?: boolean;
}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, fadeWhileClosing, ...props }: AccordionContentProps, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'overflow-hidden text-sm transition-all data-[state=open]:animate-accordion-down',
      {
        'data-[state=closed]:animate-accordion-up-fade-out': fadeWhileClosing,
        'data-[state=closed]:animate-accordion-up': !fadeWhileClosing,
      }
    )}
    {...props}
  >
    <div className={cn('pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
